import { DEFAULT_PAGINATION_OPTIONS } from '@/components/ui/pagination-controls'
import { type PaginationState } from '@tanstack/react-table'
import { useEffect, useState } from 'react'

interface UsePersistentPaginationOptions {
  key: string
  initialPagination?: PaginationState
  allowedPageSizes?: number[] // Optional: restrict to valid sizes
}

export function usePersistentPagination({
  key,
  initialPagination = { pageIndex: 0, pageSize: DEFAULT_PAGINATION_OPTIONS.defaultPageSize },
  allowedPageSizes = DEFAULT_PAGINATION_OPTIONS.pageSizes,
}: UsePersistentPaginationOptions) {
  const [pagination, setPagination] = useState<PaginationState>(() => {
    // Try to load from localStorage on initialization
    try {
      const stored = localStorage.getItem(key)
      if (stored) {
        const parsed = JSON.parse(stored)
        const pageIndex = parsed.pageIndex ?? initialPagination.pageIndex
        const pageSize = allowedPageSizes.includes(parsed.pageSize)
          ? parsed.pageSize
          : initialPagination.pageSize

        return { pageIndex, pageSize }
      }
    } catch (error) {
      console.warn('Failed to load pagination state from localStorage:', error)
    }
    return initialPagination
  })

  // Save to localStorage whenever pagination changes
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(pagination))
    } catch (error) {
      console.warn('Failed to save pagination state to localStorage:', error)
    }
  }, [key, pagination])

  return [pagination, setPagination] as const
}

// Utility function to clear persistent pagination state
export function clearPersistentPagination(key: string) {
  try {
    localStorage.removeItem(key)
  } catch (error) {
    console.warn('Failed to clear pagination state from localStorage:', error)
  }
}
